import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Maximize, Home, Square, ChevronLeft, ChevronRight, X } from 'lucide-react';
import floorPlanB103 from "../assets/f93bb4758e1c9efc04fdc00d14f203b437a48bf5.png";
import floorPlanA2BHK from "../assets/5a8ed8b2e9936af682827795c845654c8859cdbc.png";
import floorPlanB2BHK from "../assets/b77fcc04e0fe4a5ede6bf596311ef6346bc5e6a3.png";
import floorPlanB2BHKCompact from "../assets/5f5128d8515575e8e5d7dd368f1421295f340b51.png";
import floorPlan25BHK from "../assets/7c85fe7e7c7af98a94fe70deaa38570aa1231fdf.png";
import floorPlan3BHK from "../assets/c303fad59387b117481cda13d402c30c7585e489.png";
import blockAPlan from "../assets/44b88663cb94523574913c1edbf4a4bd25dfe5a5.png";
import blockAParkingPlan from "../assets/5e560183ec308b19ca9f73b991fc61c245236754.png";

import masterPlanImage from "../assets/4b4ed1b373a878ab621c2885483d5ad7f5bd9c20.png";

const FloorPlansSection = () => {
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPlanType, setSelectedPlanType] = useState("master-plan");
  
  // Reset selectedPlan when it exceeds available variations


  const floorPlans = {
    "2bhk": {
      title: "2 BHK",
      subtitle: "Perfect for young families starting their journey",
      area: "840 - 1,127",
      buildupArea: "840 - 1,127",
      uds: "376 - 604",
      price: "₹85L - 1.5 Cr",
      features: ["2 Bedrooms + 2 Bathrooms", "Spacious Living & Dining", "Balcony & Utility", "Kitchen with smart layout"],
      description: "Thoughtfully designed 2 BHK apartments with multiple layout options to suit different preferences and budgets. Each unit features optimal space utilization and modern amenities.",
      variations: [
        {
          name: "A102, A202, A302",
          image: floorPlanA2BHK,
          area: "1,127",
          buildupArea: "1,127",
          uds: "604",
          block: "Block A",
          description: "53.58% UDS — ₹60 Lakhs worth of land ownership included"
        },
        {
          name: "B101, B201, B301",
          image: floorPlanB2BHK,
          area: "998",
          buildupArea: "998",
          uds: "447",
          block: "Block B",
          description: "Efficient Block B layout with well-planned spaces and comfortable living areas"
        },
        {
          name: "B102, B202, B302",
          image: floorPlanB2BHKCompact,
          area: "840",
          buildupArea: "840",
          uds: "376",
          block: "Block B",
          description: "Compact Block B layout perfect for young professionals with smart space utilization"
        }
      ]
    },
    "25bhk": {
      title: "2.5 BHK",
      subtitle: "Enhanced Living with Study Space",
      area: "1,130",
      buildupArea: "1,130", 
      uds: "506",
      price: "₹1.4 - 1.8 Cr",
      features: ["2 Bedrooms", "2 Bathrooms", "Living/Dining", "Kitchen", "Study", "Balcony", "Utility"],
      description: "Upgraded 2.5 BHK apartments featuring an additional study room perfect for work from home or children's study area.",
      variations: [
        {
          name: "A103, A203, A303",
          image: floorPlan25BHK,
          area: "1,130",
          buildupArea: "1,130",
          uds: "506",
          block: "Block B", 
          description: "Enhanced Block A layout with dedicated study room, master bedroom, second bedroom, and spacious living areas"
        }
      ]
    },
    "3bhk": {
      title: "3 BHK",
      subtitle: "Ideal for Growing Families",
      area: "1,382",
      buildupArea: "1,382",
      uds: "740",
      price: "₹1.8 - 2.4 Cr",
      features: ["3 Bedrooms", "3 Bathrooms", "Living/Dining", "Kitchen", "Balcony", "Utility", "Foyer"],
      description: "Spacious 3 BHK homes with three comfortable bedrooms, separate bathrooms, and generous living spaces perfect for larger families.",
      variations: [
        {
          name: "A101, A201, A301",
          image: floorPlan3BHK,
          area: "1,382",
          buildupArea: "1,382",
          uds: "740",
          block: "Block A",
          description: "Premium Block A layout with master bedroom, two additional bedrooms, and expansive living/dining area"
        },
        {
          name: "B103, B203, B303",
          image: floorPlanB103,
          area: "1,382",
          buildupArea: "1,382",
          uds: "1,127",
          block: "Block B",
          description: "Spacious Block B layout with three bedrooms, multiple balconies, and well-designed living spaces"
        }
      ]
    }
  };

  const renderFloorPlanContent = (plan: any, currentVariation: any, hasMultipleVariations: boolean) => (
    <Card className="border-0 shadow-xl shadow-amber-100/20">
      <CardContent className="p-0">
        <div className="grid lg:grid-cols-2 gap-0">
          <div className="relative">
            <ImageWithFallback
              src={currentVariation?.image || ''}
              alt={`${plan.title} Floor Plan - ${currentVariation?.name || 'Floor Plan'}`}
              className="w-full h-96 lg:h-full object-contain bg-white p-4"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-teal-900/5 to-transparent"></div>
            
            {/* Navigation arrows for multiple variations */}
            {hasMultipleVariations && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-green-100 hover:scale-110 transition-all duration-200 shadow-md hover:shadow-lg group"
                  onClick={() => setSelectedPlan(selectedPlan > 0 ? selectedPlan - 1 : plan.variations.length - 1)}
                >
                  <ChevronLeft className="w-4 h-4 text-white group-hover:text-primary transition-colors duration-200" />
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-green-100 hover:scale-110 transition-all duration-200 shadow-md hover:shadow-lg group"
                  onClick={() => setSelectedPlan(selectedPlan < plan.variations.length - 1 ? selectedPlan + 1 : 0)}
                >
                  <ChevronRight className="w-4 h-4 text-white group-hover:text-primary transition-colors duration-200" />
                </Button>
              </>
            )}
            
            <div className="absolute top-4 left-4 space-y-2">
              <Badge className="bg-amber-400 text-teal-700 shadow-lg block">
                {currentVariation?.name || 'Floor Plan'}
              </Badge>
              {currentVariation?.block && (
                <Badge variant="outline" className="bg-white/90 border-teal-600 text-teal-700 shadow-sm block">
                  {currentVariation.block}
                </Badge>
              )}
            </div>
            
            <div className="absolute top-4 right-4">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="secondary" className="bg-white/90 backdrop-blur-sm hover:bg-white hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg group">
                    <Maximize className="w-4 h-4 mr-2 group-hover:text-teal-600 transition-colors duration-200" />
                    View Large
                  </Button>
                </DialogTrigger>
                <DialogContent className="!max-w-none !w-[90vw] h-[90vh] p-0 bg-white border-0 shadow-2xl flex flex-col">
                  <DialogHeader className="relative z-10 bg-gradient-to-b from-black/80 to-transparent p-6 flex-shrink-0">
                    <div className="flex items-start justify-between">
                      <DialogTitle className="text-white text-xl font-bold flex items-center gap-3">
                        <div className="flex flex-col gap-1">
                          <span>{plan.title} Floor Plan</span>
                          <div className="flex items-center gap-2 text-sm font-normal">
                            <Badge className="bg-amber-400 text-teal-700 text-xs">
                              {currentVariation?.name || 'Floor Plan'}
                            </Badge>
                            {currentVariation?.block && (
                              <Badge variant="outline" className="bg-white/90 border-teal-600 text-teal-700 text-xs">
                                {currentVariation.block}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </DialogTitle>
                      <Button 
                        size="sm"
                        variant="secondary"
                        className="bg-white/90 backdrop-blur-sm hover:bg-white hover:scale-110 transition-all duration-200 shadow-lg p-2 group"
                        onClick={() => setIsDialogOpen(false)}
                      >
                        <X className="w-6 h-6 text-gray-700 group-hover:text-red-600 transition-colors duration-200" />
                      </Button>
                    </div>
                    <DialogDescription className="sr-only">
                      Enlarged view of {plan.title} floor plan showing detailed layout, room arrangements, and specifications for {currentVariation?.name || 'this unit'}. Use arrow keys or navigation buttons to view different variations.
                    </DialogDescription>
                  </DialogHeader>
                  
                  {/* Dialog content with fixed navigation */}
                  <div className="relative flex-1 w-full">
                    {/* Scrollable image area */}
                    <div className="absolute inset-0 overflow-auto bg-gray-50 scrollbar-visible">
                      <div className="flex items-center justify-center min-h-full p-4 pb-20">
                        <ImageWithFallback
                          src={currentVariation?.image || ''}
                          alt={`${plan.title} Floor Plan - ${currentVariation?.name || 'Floor Plan'} - Large View`}
                          className="max-w-[75%] max-h-[75%] object-contain"
                          style={{ 
                            width: 'auto', 
                            height: 'auto'
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Fixed navigation controls - positioned relative to dialog, not scrollable content */}
                    {hasMultipleVariations && (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-green-100 hover:scale-110 transition-all duration-200 shadow-md hover:shadow-lg group z-10"
                          onClick={() => setSelectedPlan(selectedPlan > 0 ? selectedPlan - 1 : plan.variations.length - 1)}
                        >
                          <ChevronLeft className="w-4 h-4 text-white group-hover:text-primary transition-colors duration-200" />
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-green-100 hover:scale-110 transition-all duration-200 shadow-md hover:shadow-lg group z-10"
                          onClick={() => setSelectedPlan(selectedPlan < plan.variations.length - 1 ? selectedPlan + 1 : 0)}
                        >
                          <ChevronRight className="w-4 h-4 text-white group-hover:text-primary transition-colors duration-200" />
                        </Button>
                        
                        {/* Fixed variation indicators */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                          {plan.variations.map((variation: any, index: number) => (
                            <button
                              key={index}
                              className={`w-3 h-3 rounded-full transition-all duration-200 border-2 hover:scale-125 ${
                                selectedPlan === index ? 'bg-amber-400 border-amber-400 scale-110' : 'bg-white/70 border-white hover:bg-white hover:border-amber-300 shadow-sm hover:shadow-md'
                              }`}
                              onClick={() => setSelectedPlan(index)}
                              title={`${variation.name} - ${variation.block || ''}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Fixed specification panel at bottom */}
                  <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600 mb-1">Flat Numbers</div>
                          <div className="font-semibold text-teal-700">
                            {currentVariation?.name}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600 mb-1">Buildup Area</div>
                          <div className="font-semibold text-gray-900">
                            {currentVariation?.buildupArea || plan.buildupArea} Sq Ft
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600 mb-1">UDS Area</div>
                          <div className="font-semibold text-gray-900">
                            {currentVariation?.uds || plan.uds} Sq Ft
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600 mb-1">Price Range</div>
                          <div className="font-semibold text-amber-700">
                            {plan.price}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            {/* Variation dots for multiple layouts */}
            {hasMultipleVariations && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {plan.variations.map((variation: any, index: number) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full transition-all duration-200 border-2 hover:scale-125 ${
                      selectedPlan === index ? 'bg-amber-400 border-amber-400 scale-110' : 'bg-white/70 border-white hover:bg-white hover:border-amber-300 shadow-sm hover:shadow-md'
                    }`}
                    onClick={() => setSelectedPlan(index)}
                    title={`${variation.name} - ${variation.block || ''}`}
                  />
                ))}
              </div>
            )}
          </div>
        
          <div className="lg:p-12 w-[100%] p-[20px]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">
                  {plan.title}
                </h3>
                <p className="text-lg text-gray-600">{plan.subtitle}</p>
              </div>
              <Badge variant="outline" className="border-amber-200 text-teal-700">
                <Home className="w-4 h-4 mr-1" />
                {plan.title}
              </Badge>
            </div>

            <div className="grid grid-cols-3 gap-6 mb-8">
              <div>
                <div className="flex items-center text-gray-600 mb-2">
                  <Square className="w-5 h-5 mr-2" />
                  Buildup Area
                </div>
                <div className="text-2xl font-bold text-gray-900 text-[16px] w-[90%]">
                  {plan.title === "2.5 BHK" ? "1325" : (currentVariation?.buildupArea || plan.buildupArea)} Sq Ft.
                </div>
              </div>
              <div>
                <div className="text-gray-600 mb-2">UDS</div>
                <div className="text-2xl font-bold text-gray-900 text-[16px]">
                  {plan.title === "2.5 BHK" ? "710" : (currentVariation?.uds || plan.uds)} Sq Ft.
                </div>
              </div>
              <div>
                <div className="text-gray-600 mb-2">Price</div>
                <div className="text-2xl font-bold text-amber-700 text-[16px]">
                  Get Launch Price
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h4 className="font-semibold text-gray-900 mb-4">Key Features:</h4>
              <div className="grid grid-cols-2 gap-3">
                {(plan.title === "2.5 BHK" ? [
                  "2 Bedrooms + Study Room",
                  "2 Bathrooms", 
                  "Enhanced Living Area",
                  "Private Balcony"
                ] : plan.title === "3 BHK" ? [
                  "3 Bedrooms + 3 Bathrooms",
                  "Expansive Living & Dining Area",
                  "Two Balconies",
                  "Separate Utility Area"
                ] : plan.features).map((feature: string, index: number) => (
                  <div key={index} className="flex items-center text-gray-600">
                    <div className="w-2 h-2 bg-teal-600 rounded-full mr-3"></div>
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            <p className="text-teal-800 bg-teal-50 p-4 rounded-lg border-l-4 border-r-4 border-teal-600 mb-6 font-medium w-[100%]">
              {plan.title === "2.5 BHK" ? (
                <span className="block text-center" dangerouslySetInnerHTML={{
                  __html: "53.58% UDS — ₹71 Lakhs worth of land ownership included".replace(
                    /53\.58% UDS/g,
                    '<span class="font-bold text-white px-6 py-1 rounded-md inline-block min-w-[150px] text-center" style="background: linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3); background-size: 200% 200%; animation: rainbow 2s ease-in-out infinite;">53.58% UDS</span>'
                  ).replace(
                    /— ₹71 Lakhs worth of land ownership included/g,
                    '<br><span class="font-bold text-green-700">— ₹71 Lakhs worth of land ownership included</span>'
                  )
                }} />
              ) : plan.title === "3 BHK" ? (
                <span className="block text-center" dangerouslySetInnerHTML={{
                  __html: "53.58% UDS — ₹74 Lakhs worth of land ownership included".replace(
                    /53\.58% UDS/g,
                    '<span class="font-bold text-white px-6 py-1 rounded-md inline-block min-w-[150px] text-center" style="background: linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3); background-size: 200% 200%; animation: rainbow 2s ease-in-out infinite;">53.58% UDS</span>'
                  ).replace(
                    /— ₹74 Lakhs worth of land ownership included/g,
                    '<br><span class="font-bold text-green-700">— ₹74 Lakhs worth of land ownership included</span>'
                  )
                }} />
              ) : (
                <span className="block m-[0px] text-center" dangerouslySetInnerHTML={{
                  __html: (currentVariation?.description || plan.description)?.replace(
                    /53\.58% UDS/g,
                    '<span class="font-bold text-white px-6 py-1 rounded-md inline-block min-w-[150px] text-center" style="background: linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3); background-size: 200% 200%; animation: rainbow 2s ease-in-out infinite;">53.58% UDS</span>'
                  ).replace(
                    /— ₹60 Lakhs worth of land ownership included/g,
                    '<br><span class="font-bold text-green-700 px-2 py-1 rounded-md text-center block w-full">— ₹60 Lakhs worth of land ownership included</span>'
                  ) || ''
                }} />
              )}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {/* <Button className="flex-1 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 hover:scale-105 transition-all duration-200 text-white group">
                <Download className="w-4 h-4 mr-2 group-hover:animate-bounce" />
                Download Plan
              </Button> */}
              <Button 
                variant="outline" 
                className="border-amber-300 text-teal-700 hover:bg-amber-50 hover:scale-105 hover:border-amber-400 transition-all duration-200 px-8"
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Schedule Visit
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section id="floor-plans" className="py-20 bg-gradient-to-br from-amber-100 via-amber-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-[110%]">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-amber-100 text-teal-700">
            Floor Plans
          </Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Perfect Home
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Smart layouts, spacious rooms, and natural light — crafted for modern living.
          </p>

          <div className="flex justify-center mt-8">
            <ToggleGroup 
              type="single" 
              value={selectedPlanType} 
              onValueChange={setSelectedPlanType}
              className="bg-white border border-amber-200 rounded-lg p-1 shadow-sm"
            >
              <ToggleGroupItem 
                value="master-plan" 
                className="data-[state=on]:bg-amber-400 data-[state=on]:text-teal-700 hover:bg-amber-50 text-gray-700 px-6 py-2 transition-all duration-200"
              >
                Master Plan
              </ToggleGroupItem>
              <ToggleGroupItem 
                value="block-a" 
                className="data-[state=on]:bg-amber-400 data-[state=on]:text-teal-700 hover:bg-amber-50 text-gray-700 px-6 py-2 transition-all duration-200"
              >
                Block A
              </ToggleGroupItem>
              <ToggleGroupItem 
                value="block-b" 
                className="data-[state=on]:bg-amber-400 data-[state=on]:text-teal-700 hover:bg-amber-50 text-gray-700 px-6 py-2 transition-all duration-200"
              >
                Block B
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {/* Master Plan View */}
        {selectedPlanType === "master-plan" && (
          <Card className="border-0 shadow-xl shadow-amber-100/20">
            <CardContent className="p-8 lg:p-12 text-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Blucon Sunrise Master Plan</h3>
              <p className="text-lg text-gray-600 mb-8">Comprehensive master plan showing Block A and Block B layouts with detailed specifications.</p>
              <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                <ImageWithFallback
                  src={masterPlanImage}
                  alt="Blucon Sunrise Master Plan - Block A and Block B Layout"
                  className="w-[43%] h-auto object-contain mx-auto"
                />
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Block A View */}
        {selectedPlanType === "block-a" && (
          <Tabs defaultValue="layout" className="w-full">
            <TabsList className="grid w-full grid-cols-5 max-w-4xl mx-auto mb-12 bg-amber-50">
              <TabsTrigger value="layout" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                Master Plan
              </TabsTrigger>
              <TabsTrigger value="2bhk" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                2 BHK
              </TabsTrigger>
              <TabsTrigger value="25bhk" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                2.5 BHK
              </TabsTrigger>
              <TabsTrigger value="3bhk" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                3 BHK
              </TabsTrigger>
              <TabsTrigger value="parking" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
                Car Parking
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="layout">
              <Card className="border-0 shadow-xl shadow-amber-100/20">
                <CardContent className="p-8 lg:p-12 text-center">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Block A Layout Plan</h3>
                  <p className="text-lg text-gray-600 mb-8">Detailed layout plan for Block A showing unit positions and common areas.</p>
                  <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                    <ImageWithFallback
                      src={blockAPlan}
                      alt="Block A Floor Plan with Area Statement"
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {Object.entries(floorPlans).map(([key, plan]) => {
              const blockAVariations = plan.variations.filter(v => v.block === "Block A");
              if (blockAVariations.length === 0) return null;
              
              const currentVariation = blockAVariations[0];
              
              return (
                <TabsContent key={key} value={key}>
                  {renderFloorPlanContent(plan, currentVariation, false)}
                </TabsContent>
              );
            })}
            
            {/* Add dedicated 2.5 BHK tab content for Block A */}
            <TabsContent value="25bhk">
              {renderFloorPlanContent(floorPlans["25bhk"], floorPlans["25bhk"].variations[0], false)}
            </TabsContent>
            
            <TabsContent value="parking">
              <Card className="border-0 shadow-xl shadow-amber-100/20">
                <CardContent className="p-8 lg:p-12 text-center">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Block A Car Parking</h3>
                  <p className="text-lg text-gray-600 mb-8">Parking layout and allocation details for Block A residents.</p>
                  <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                    <ImageWithFallback
                      src={blockAParkingPlan}
                      alt="Block A Parking Layout Plan"
                      className="w-4/5 h-auto object-contain mx-auto"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
        
        {/* Block B View */}
        {selectedPlanType === "block-b" && (
          <div className="w-full">
            <Card className="border-0 shadow-xl shadow-amber-100/20">
              <CardContent className="p-8 lg:p-16 text-center">
                <div className="max-w-2xl mx-auto">
                  {/* Success Icon */}
                  <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">
                    Block B is Fully Booked!
                  </h3>
                  
                  {/* Message */}
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                    Thank you for your overwhelming response! All units in Block B have been successfully reserved. 
                    Don't miss out on the amazing units still available in Block A.
                  </p>
                  
                  {/* CTA Button */}
                  <Button 
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                    onClick={() => setSelectedPlanType("block-a")}
                  >
                    Explore Block A Units
                  </Button>
                  
                  {/* Additional Info */}
                  <div className="mt-8 p-6 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                    <p className="text-amber-800 font-medium">
                      Block A offers the same premium amenities and layouts with excellent availability. 
                      <br />
                      <span className="font-bold">Reserve your dream home today!</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default FloorPlansSection;